"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import useForm from "@/shared/hooks/use-form"
import { privacyAndCookiesPolicies, termsAndConditions } from "@/shared/utils/links"
import { PAGE_SHOWN_EVENT } from "@/shared/utils/page-shown"
import { isSmqPopinDismissed, markSmqPopinDismissed } from "@/shared/utils/storage"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import Lottie from "lottie-react"
import { Check, Loader2, X } from "lucide-react"
import Image, { getImageProps } from "next/image"
import Link from "next/link"
import { usePostHog } from "posthog-js/react"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Controller, SubmitHandler } from "react-hook-form"
import { Trans } from "react-i18next"

import MailboxLottie from "../../../../public/lottie/Send.json"
import { showFeedbackToast } from "./feedback-toast"
import { playCloseMorph, playOpenMorph } from "./liquid-morph"
import * as styles from "./send-my-quote-v2.styled"
import { sendMyQuoteSchema, SendMyQuoteSchema } from "./send-my-quote.schema"
import { errorBanner } from "./send-my-quote.styled"
import { classifyError, useSendMyQuote } from "./use-send-my-quote"

const AUTO_OPEN_DELAY_MS = 10_000

const MAILBOX_ILLUSTRATION = { src: "/img/illus/Mailbox.svg", width: 48, height: 48 }

/** closed → (10s timer or CTA click) → open → (X / Escape / sent) → closing → closed */
type Phase = "closed" | "open" | "closing"

/**
 * V2 of the "Send my quote" entry point (DLM-1770), AB-tested against
 * `SendMyQuote`: a non-modal card that auto-opens 10s after page entry,
 * collapses into the paper-plane CTA with a liquid morph on close, and never
 * auto-reopens once dismissed for the session. Unlike V1, the CTA plays no
 * idle animation.
 */
export const SendMyQuoteV2 = () => {
  const [phase, setPhase] = useState<Phase>("closed")
  const cardRef = useRef<HTMLDivElement | null>(null)
  const ctaRef = useRef<HTMLButtonElement | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const morphRef = useRef<Animation[] | null>(null)
  const focusEmailOnOpen = useRef(false)
  const restoreFocusOnClose = useRef(false)
  const [state, send] = useActor(newSubscriptionFlowService)
  const { t, i18n } = useClientTranslation("global", { keyPrefix: "send-my-quote" })
  const posthog = usePostHog()
  const { mutate: sendQuote, isPending: isLoading, errorKind } = useSendMyQuote()

  const quoteId = state.context.quote?.meta?.quote_id
  const userId = state.context.quote?.meta?.user_id

  const { handleSubmit, formState, register, control, watch } = useForm(
    sendMyQuoteSchema,
    {
      mode: "onTouched",
      defaultValues: {
        email_sent_to: state.context.quote?.owner?.email ?? undefined,
      },
    }
  )

  // Warm the illustration cache so the first (auto-)open doesn't decode the
  // image mid-morph — the card only mounts when it opens.
  useEffect(() => {
    const { props } = getImageProps({ ...MAILBOX_ILLUSTRATION, alt: "" })
    new window.Image().src = props.src
  }, [])

  const closeCard = useCallback((options?: { restoreFocus?: boolean }) => {
    restoreFocusOnClose.current = options?.restoreFocus ?? false
    markSmqPopinDismissed()
    setPhase((current) => (current === "open" ? "closing" : current))
  }, [])

  // Auto-open once per session: 10s after the page is actually visible, until
  // the user closes the card (the component stays mounted across pages).
  // Arming on mount is only a fallback — the countdown restarts on each
  // PAGE_SHOWN_EVENT (PageTransition mount, end of the /prepackages pricing
  // loader), because the URL commits before slow pages finish rendering and
  // counting from the URL change would open the card too early for the user.
  useEffect(() => {
    if (phase !== "closed" || isSmqPopinDismissed()) {
      return
    }
    let id: number | undefined
    const arm = () => {
      window.clearTimeout(id)
      id = window.setTimeout(() => setPhase("open"), AUTO_OPEN_DELAY_MS)
    }
    arm()
    window.addEventListener(PAGE_SHOWN_EVENT, arm)
    return () => {
      window.clearTimeout(id)
      window.removeEventListener(PAGE_SHOWN_EVENT, arm)
    }
  }, [phase])

  // Morph the card out of the CTA. Focus only moves on manual opens: stealing
  // it on auto-open would interrupt whatever the user is doing.
  useLayoutEffect(() => {
    if (phase !== "open" || !cardRef.current) {
      return
    }
    morphRef.current = playOpenMorph(cardRef.current, ctaRef.current)
    if (focusEmailOnOpen.current) {
      focusEmailOnOpen.current = false
      emailInputRef.current?.focus()
    }
    return () => {
      morphRef.current?.forEach((animation) => animation.cancel())
      morphRef.current = null
    }
  }, [phase])

  // Collapse the card into the CTA, then unmount it.
  useLayoutEffect(() => {
    if (phase !== "closing") {
      return
    }
    const animations = cardRef.current ? playCloseMorph(cardRef.current, ctaRef.current) : null
    if (!animations) {
      setPhase("closed")
      return
    }
    morphRef.current = animations
    animations[0].onfinish = () => setPhase("closed")
    return () => {
      animations.forEach((animation) => animation.cancel())
      morphRef.current = null
    }
  }, [phase])

  useEffect(() => {
    if (phase !== "open") {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeCard({ restoreFocus: true })
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [phase, closeCard])

  useEffect(() => {
    if (phase === "closed" && restoreFocusOnClose.current) {
      restoreFocusOnClose.current = false
      ctaRef.current?.focus()
    }
  }, [phase])

  // Only surface the format error once the user typed something: flagging an
  // empty field on a mere focus/blur is premature (submit stays disabled anyway).
  const emailError = watch("email_sent_to")
    ? formState.errors.email_sent_to?.message
    : undefined

  const onSubmit: SubmitHandler<SendMyQuoteSchema> = (values) => {
    if (!quoteId || !userId) return

    const locale = i18n.language

    // DLM-1777: submit-click event for the tested entry point (DE health only).
    posthog?.capture("cta_clicked", {
      CTA_name: "send_quote_submit_form_2",
      quote_id: quoteId,
      user_id: userId,
      locale,
      smq_variant: "v2",
    })

    sendQuote(
      { quote_id: quoteId, user_id: userId, email_sent_to: values.email_sent_to, locale },
      {
        onSuccess: () => {
          send({ type: "SET_EMAIL_SENT_TO", email: values.email_sent_to })
          posthog?.capture("send_quote_feature", {
            quote_id: quoteId,
            user_id: userId,
            locale,
            smq_variant: "v2",
          })
          showFeedbackToast("success", t("success"))
          closeCard()
        },
        onError: (error) => {
          // Cooldown keeps its inline banner + disabled CTA; only the generic error surfaces as a toast.
          if (classifyError(error) === "generic") {
            showFeedbackToast("error", t("error.generic"))
          }
        },
      }
    )
  }

  const tcLinks = {
    link1: (
      <Link
        href={new URL(termsAndConditions.link)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-white underline"
      />
    ),
    link2: (
      <Link
        href={new URL(privacyAndCookiesPolicies.link)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-white underline"
      />
    ),
  }

  const emailRegistration = register("email_sent_to")

  return (
    <>
      <button
        ref={ctaRef}
        type="button"
        onClick={() => {
          if (phase !== "closed") return
          posthog?.capture("cta_clicked", {
            CTA_name: "send_quote_breadcrumb",
            quote_id: quoteId,
            user_id: userId,
            locale: i18n.language,
            smq_variant: "v2",
          })
          focusEmailOnOpen.current = true
          setPhase("open")
        }}
        aria-label={t("button-label")}
        aria-expanded={phase === "open"}
        className={styles.triggerButton}
      >
        <Lottie animationData={MailboxLottie} loop={false} autoplay={false} className="h-5 w-5" />
      </button>

      {phase !== "closed" ? (
        <div className={styles.cardPositioner}>
          <div
            ref={cardRef}
            role="dialog"
            aria-label={t("title")}
            className={styles.card(phase === "closing")}
          >
            <div className={styles.headerRow}>
              <Image {...MAILBOX_ILLUSTRATION} alt="" aria-hidden priority />
              <button
                type="button"
                onClick={() => closeCard({ restoreFocus: true })}
                className={styles.closeButton}
              >
                <X className="h-5 w-5 text-white" aria-hidden />
                <span className="sr-only">{t("close")}</span>
              </button>
            </div>

            <div className={styles.titleWrapper}>
              <h2 className={styles.title}>{t("title")}</h2>
              <p className={styles.description}>{t("description")}</p>
            </div>

            <form
              id="send-my-quote-v2-form"
              onSubmit={(event) => void handleSubmit(onSubmit)(event)}
              className={styles.form}
            >
              <div className={styles.emailField}>
                <input
                  {...emailRegistration}
                  ref={(element) => {
                    emailRegistration.ref(element)
                    emailInputRef.current = element
                  }}
                  type="email"
                  placeholder={t("email-placeholder")}
                  className={styles.emailInput(!!emailError)}
                />
                {emailError ? <p className={styles.emailError}>{emailError}</p> : null}
              </div>

              <Controller
                control={control}
                name="tcAccepted"
                render={({ field }) => (
                  <label htmlFor="send-my-quote-v2-tc" className={styles.consent}>
                    <input
                      id="send-my-quote-v2-tc"
                      type="checkbox"
                      className="peer sr-only"
                      checked={field.value === true}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                    <span className={styles.checkboxBox(field.value === true)} aria-hidden>
                      {field.value === true ? (
                        <Check className="h-4 w-4 text-blurple-500" strokeWidth={3} />
                      ) : null}
                    </span>
                    <span className={styles.consentText}>
                      <Trans t={t} i18nKey="consent-label" components={tcLinks} />
                    </span>
                  </label>
                )}
              />

              {errorKind === "cooldown" ? (
                <div className={errorBanner} role="alert">
                  {t("error.cooldown")}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={!formState.isValid || errorKind === "cooldown" || isLoading}
                className={styles.submitButton}
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : t("cta")}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  )
}
